import {
  PlusIcon,
  ShareIcon,
  ChevronRightIcon,
  PaperAirplaneIcon,
  HeartIcon as SolidHeartIcon,
} from "@heroicons/react/24/solid";
import { HeartIcon as OutlineHeartIcon, CalendarDaysIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, useRef, useMemo } from "react";
import { listenToChatMessages, listenToUpcomingEventsSubGroup, sendMessage } from "../../services/firestore/SGloopService";
import { doc, updateDoc, arrayUnion, arrayRemove, getFirestore } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import EventList from "./EventList";
import NoUserModal from "../Modal/NoUserModal";
import NewMembershipModal from "../Modal/NewMembershipModal";

export default function SubGroupConversation({ selectedSubGroup, mainLabel, setSelectedSubGroup }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [chat, setChat] = useState([]);
  const [openMembershipModal, setOpenMembershipModal] = useState(false);
  const [eventsData, setEventsData] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [openNoUserModal, setOpenNoUserModal] = useState(false);
  const [active, setActive] = useState("Chat");

  const { user, userRef } = useAuth();
  const db = getFirestore(firebaseApp);
  const chatContainerRef = useRef(null);

  // Derived values
  const isAdmin = useMemo(
    () => selectedSubGroup?.adminMember?.some(admin => admin.id === userRef?.id),
    [selectedSubGroup, userRef]
  );

  const isMember = useMemo(
    () => selectedSubGroup?.subMembershipSubscription?.some(member => member.id === userRef?.id),
    [selectedSubGroup, userRef]
  );

  // Like/unlike a message
  const toggleLike = async (messageId, hasLiked) => {
    if (!userRef) return;

    const messageRef = doc(
      db,
      "oneSGChats",
      selectedSubGroup.oneSGchats.id,
      "OnesgChatMessage",
      messageId
    );

    await updateDoc(messageRef, {
      likesFromUsers: hasLiked ? arrayRemove(userRef) : arrayUnion(userRef),
    });
  };

  // Join subgroup
  const handleJoin = async () => {
    if (!userRef) {
      setOpenNoUserModal(true);
      return;
    }

    try {
      await updateDoc(doc(db, selectedSubGroup.ref.path), {
        subMembershipSubscription: arrayUnion(userRef),
      });

      // Optimistic UI update
      setSelectedSubGroup(prev => ({
        ...prev,
        subMembershipSubscription: [...(prev.subMembershipSubscription || []), userRef],
      }));

      if (selectedSubGroup?.MembershipForm?.length > 0) {
        setOpenMembershipModal(true);
      }
    } catch (err) {
      console.error("Error joining group:", err);
    }
  };

  // Send message
  const handleSend = () => {
    if (!newMessage.trim() || !userRef) return;
    sendMessage(selectedSubGroup.oneSGchats.id, newMessage, userRef, selectedSubGroup.ref);
    setNewMessage("");
  };

  // Load tab from URL (initial load)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params.get("tab");

    if (tabFromUrl === "Events" || tabFromUrl === "Chat") {
      setActive(tabFromUrl);
    }
  }, []);

  // Update URL when switching tabs
  useEffect(() => {
    if (!selectedSubGroup) return;

    const params = new URLSearchParams(window.location.search);
    params.set("tab", active);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({ path: newUrl }, "", newUrl);
  }, [active, selectedSubGroup]);

  // Listeners (no more forced "Chat")
  useEffect(() => {
    if (!selectedSubGroup) return;

    const unsubscribeChat = listenToChatMessages(
      selectedSubGroup.oneSGchats.id,
      setChat
    );

    const unsubscribeEvents = listenToUpcomingEventsSubGroup(
      selectedSubGroup,
      setEventsData
    );

    return () => {
      unsubscribeChat && unsubscribeChat();
      unsubscribeEvents && unsubscribeEvents();
    };
  }, [selectedSubGroup]);

  // Auto scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chat]);

  return (
    <>
      {openNoUserModal && <NoUserModal onClose={() => setOpenNoUserModal(false)} />}
      {openMembershipModal && (
        <NewMembershipModal
          onClose={() => setOpenMembershipModal(false)}
          selectedGroups={selectedSubGroup}
        />
      )}

      <div className={`${selectedSubGroup ? "block md:block" : "hidden md:hidden"} md:w-2/3 p-4 overflow-y-auto`}>
        {selectedSubGroup && (
          <>
            {/* Breadcrumb */}
            <nav className="flex pb-4 justify-between" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
                <li>
                  <a
                    href="/"
                    className="text-xs sm:text-sm font-medium text-gray-700 hover:text-blue-600 flex items-center"
                  >
                    Home
                  </a>
                </li>

                <li>
                  <div className="flex items-center">
                    <ChevronRightIcon className="w-3 h-3 text-gray-400 mx-1" />
                    <span
                      className="text-xs sm:text-sm font-medium text-gray-600 hover:text-blue-600 cursor-pointer"
                      onClick={() => {
                        setSelectedSubGroup(null);
                        const params = new URLSearchParams(window.location.search);
                        params.delete("subId");
                        const newUrl = `${window.location.pathname}?${params.toString()}`;
                        window.history.pushState({ path: newUrl }, "", newUrl);
                      }}
                    >
                      {mainLabel.areaName}
                    </span>
                  </div>
                </li>

                <li>
                  <div className="flex items-center">
                    <ChevronRightIcon className="w-3 h-3 text-gray-400 mx-1" />
                    <span className="text-xs sm:text-sm font-medium text-gray-400">
                      {selectedSubGroup.subGroupName}
                    </span>
                  </div>
                </li>
              </ol>

              {isAdmin ? (
                <span className="flex items-center justify-center text-xs sm:text-sm px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                  Admin
                </span>
              ) : isMember ? (
                <span className="flex items-center justify-center text-xs sm:text-sm px-2 py-1 bg-green-100 text-green-800 rounded-full">
                  Joined
                </span>
              ) : (
                <button
                  onClick={handleJoin}
                  className="flex items-center justify-center text-xs sm:text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200"
                >
                  <UserPlusIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Join
                </button>
              )}
            </nav>

            {/* Active Tabs */}
            <div className="flex mb-4">
              <button
                onClick={() => setActive("Chat")}
                className={`flex-1 px-4 py-2 rounded-l-lg border border-gray-300 font-semibold text-sm sm:text-base ${
                  active === "Chat"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Chats
              </button>

              <button
                onClick={() => setActive("Events")}
                className={`flex-1 px-4 py-2 rounded-r-lg border border-gray-300 font-semibold text-sm sm:text-base ${
                  active === "Events"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center justify-center gap-x-2">
                  <CalendarDaysIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>{eventsData.length} Events</span>
                </div>
              </button>
            </div>

            {/* Chats */}
            {active === "Chat" && (
              <div className="flex flex-col h-[79vh]">
                <div ref={chatContainerRef} className="flex-1 overflow-y-auto space-y-4 pr-2">
                  {chat.map(item => {
                    const hasLiked = userRef
                      ? item.likesFromUsers?.some(ref => ref.id === userRef.id)
                      : false;

                    return (
                      <div key={item.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex gap-3">
                            <img
                              src={item.sender?.photo_url}
                              alt="User"
                              className="w-10 h-10 rounded-full"
                            />
                            <div>
                              {item.imageMessage && (
                                <img
                                  src={item.imageMessage}
                                  alt="attachment"
                                  className="w-full h-auto max-h-[600px] rounded-md object-cover"
                                />
                              )}
                              {item.message && (
                                <div className="whitespace-pre-line break-words text-left">{item.message}</div>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {item.timestamp?.toDate
                                  ? item.timestamp.toDate().toLocaleString()
                                  : item.timestamp}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3">
                            {hasLiked ? (
                              <SolidHeartIcon
                                className="w-5 h-5 text-red-500 cursor-pointer"
                                onClick={() => toggleLike(item.id, hasLiked)}
                              />
                            ) : (
                              <OutlineHeartIcon
                                className="w-5 h-5 text-gray-500 cursor-pointer"
                                onClick={() => toggleLike(item.id, hasLiked)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-2 border-t pt-2">
                  <div className="flex items-center border rounded-lg px-4 py-2">
                    <input
                      type="text"
                      className="flex-1 outline-none"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                    />
                    <PaperAirplaneIcon
                      className="w-5 h-5 text-blue-500 cursor-pointer"
                      onClick={handleSend}
                    />
                  </div>
                </div>
              </div>
            )}

            {active === "Events" && (
              <EventList eventsData={eventsData} source={selectedSubGroup} />
            )}
          </>
        )}
      </div>
    </>
  );
}
